import { MdCheck } from '@react-icons/all-files/md/MdCheck'
import React, { useCallback, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'

import mixpanel from '@disco/api/mixpanel'
import { LYRIC_TRANSCRIPTION } from '@disco/constants/FeatureTypes'
import {
  sendTranscriptionFeedback,
  transcribeTracks,
} from '@disco/state-management/actions/ServerActions'
import {
  getLyricTranscriptionQuotaLeft,
  isFeatureEnabled,
} from '@disco/state-management/selectors/business'
import {
  getConstanceSettings,
  isLyricTranscriptionTermsAccepted,
} from '@disco/state-management/selectors/user'

import type { Track } from 'types/track'

import CopyMetadataButton from 'components/common/tracks/copy-metadata-button'
import type { FeedbackDialogSelection } from 'components/dialogs/FeedbackDialog'
import FeedbackDialog from 'components/dialogs/FeedbackDialog'
import {
  AiSuggestTextArea,
  Box,
  Dialog,
  Flex,
  Text,
  TextArea,
} from 'components/ds'
import DialogFooter from 'components/modals/dialog-footer'
import Button from 'components/ui/button'
import ButtonContainer from 'components/ui/button-container'
import InlineLink from 'components/ui/inline-link'

import { useDispatch, useSelector } from 'hooks/use-redux'

type Props = {
  track: Track
  saving?: boolean
  disabled?: boolean
  value: string
  initialValue?: string
  onChange: (value: string) => void
  onClose?: () => void
  onSubmit: (...args: Array<any>) => any
  /** Hide the footer's "Copy track info" when the surrounding chrome
   *  already offers it (e.g. the track edit modal header). */
  hideCopyMetadata?: boolean
}

/**
 * EditLyrics component for editing track lyrics with AI transcription support
 *
 * A functional component that provides an interface for editing track lyrics with
 * integrated AI transcription capabilities. Features confidence scoring, feedback
 * collection, quota management, and terms acceptance flow for AI services.
 *
 * @param props - Component props
 * @param props.track - Track object containing metadata and ID
 * @param props.saving - Whether lyrics are currently being saved
 * @param props.disabled - Whether editing is disabled
 * @param props.value - Current lyrics text value
 * @param props.onChange - Callback for lyrics text changes
 * @param props.onClose - Callback for closing the lyrics editor
 * @param props.onSubmit - Callback for submitting/saving lyrics
 * @returns JSX element containing the lyrics editing interface with AI features
 */
const EditLyrics = ({
  track,
  saving,
  disabled,
  value,
  initialValue,
  onChange,
  onClose,
  onSubmit,
  hideCopyMetadata,
}: Props) => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const [savedValue, setSavedValue] = useState(initialValue ?? value)
  const [submitting, setSubmitting] = useState(false)
  const [showFeedback, setShowFeedback] = useState(false)
  const lyricTranscriptionTermsAccepted = useSelector(
    isLyricTranscriptionTermsAccepted,
  )

  // Get the lowest confidence setting from constance
  const constanceSettings = useSelector(getConstanceSettings)
  const lowestConfidence =
    constanceSettings?.LYRIC_TRANSCRIPTION_LOWEST_CONFIDENCE || 0
  const handleChange = useCallback(
    event => {
      onChange(event.target.value)
    },
    [onChange],
  )
  const transcriptionEnabled = useSelector(state =>
    isFeatureEnabled(state, {
      featureName: LYRIC_TRANSCRIPTION,
    }),
  )
  const lyricTranscriptionQuotaLeft = useSelector(
    getLyricTranscriptionQuotaLeft,
  )

  const isAiSource = track.metadata_extras?.Lyrics?.source === 'ai'
  const aiEnabled = transcriptionEnabled || isAiSource

  const handleTranscribe = useCallback(() => {
    if (!lyricTranscriptionTermsAccepted) {
      navigate(`/explorer/${track.id}/lyric-transcription-terms-dialog`, {
        replace: true,
        state: {
          returnTo: `${location.pathname}?tab=1`,
        },
      })
    }
    if (lyricTranscriptionTermsAccepted) {
      setSubmitting(true)
      dispatch(transcribeTracks([track.id])).then(() => {
        setSubmitting(false)
      })
    }

    mixpanel.sendPureEvent('Click button', {
      context: 'Lyric transcription',
      copy: 'Transcribe',
      type: 'Transcribe lyrics',
      track_id: track.id,
    })
  }, [dispatch, track.id])

  const handleSubmit = useCallback(() => {
    onSubmit()
    setSavedValue(value)
  }, [onSubmit, value])
  const changed = value !== savedValue
  const lyricsMetadata = track.metadata_extras?.Lyrics
  const aiBusy = lyricsMetadata?.ai_transcription_in_progress || submitting
  const confidence =
    lyricsMetadata?.source === 'ai' && lyricsMetadata?.confidence
      ? parseFloat(lyricsMetadata.confidence)
      : undefined
  const textAreaProps = {
    disabled,
    value,
    rows: 12,
    // `grow` sizes the field to its content via an inline height, which
    // fights the fill layout below; let it fill and scroll internally.
    grow: false,
    onChange: handleChange,
  }

  const handleAcceptTranscription = useCallback(() => {
    setSubmitting(true)
    dispatch(sendTranscriptionFeedback(track.id, 'positive')).then(() => {
      setSubmitting(false)
    })
  }, [dispatch, track.id])

  const handleRejectTranscription = useCallback(
    (selection?: FeedbackDialogSelection) => {
      setSubmitting(true)
      dispatch(
        sendTranscriptionFeedback(
          track.id,
          'negative',
          selection?.value,
          selection?.freeText,
        ),
      ).then(() => {
        setSubmitting(false)
      })
    },
    [dispatch, track.id],
  )

  const handleClickUpgrade = () => {
    mixpanel.sendPureEvent('Click link', {
      Context: 'Lyric transcription',
      Copy: 'Upgrade',
      Type: 'Upgrade to unlimited Lyric transcription',
      Destination: '/settings/billing',
      ['Track ID']: track.id,
    })
  }

  const isNoResult = confidence === 1 && value === ''

  const dialogProps = {
    title: 'Confirm deletion',
    description:
      'Sorry, looks like we got this wrong. We will reset the lyrics field for you in a moment.',
    options: isNoResult
      ? [{ label: 'Other', value: 'other', freeText: true }]
      : [
          {
            label: 'Too many incorrect or missing words',
            value: 'too_many_incorrect_or_missing_words',
          },
          {
            label: "Language wasn't identified correctly",
            value: 'language_was_not_identified_correctly',
          },
          {
            label: "Words or phrases were added that aren't in the lyrics",
            value: 'words_or_phrases_were_added_that_are_not_in_the_lyrics',
          },
          {
            label: 'Formatting issues, e.g. line breaks and punctuation',
            value: 'formatting_issues',
          },
          { label: 'Other', value: 'other', freeText: true },
        ],
    optionLabel: 'Mind telling us what wasn’t good enough?',
    optionPrompt: 'Choose something',
    freeTextLabel: 'Can you tell us more?',
    confirmButtonLabel: 'Proceed',
    cancelButtonLabel: 'Cancel',
    initialOption: isNoResult ? 'other' : undefined,
  }

  return (
    <>
      <Box mb="4" className="EditLyrics_Editor">
        {aiEnabled ? (
          <>
            <AiSuggestTextArea
              {...textAreaProps}
              onSuggest={handleTranscribe}
              onAccept={handleAcceptTranscription}
              onReject={() => setShowFeedback(true)}
              onDismiss={() =>
                handleRejectTranscription({
                  value: 'low_confidence',
                  freeText: '',
                })
              }
              processing={aiBusy}
              confidence={confidence}
              lowestConfidence={lowestConfidence}
              aiDisabled={track.read_only || track.video}
              quotaAvailable={lyricTranscriptionQuotaLeft}
              quotaText={
                <>
                  <InlineLink
                    to={{
                      pathname: '/settings/billing',
                      search: '?upgrade=lyric-transcription',
                    }}
                    onClick={handleClickUpgrade}
                  >
                    Upgrade
                  </InlineLink>{' '}
                  for unlimited transcriptions.{' '}
                  <InlineLink
                    to="https://disco.ac/feature-lyric-transcription"
                    target="_blank"
                  >
                    Learn more
                  </InlineLink>
                </>
              }
              generationDisabledTooltipText="You are out of free transcriptions"
              processingText="AI transcription in progress... we will notify you when finished."
              emptyText="Looks like there are no lyrics yet. Do you want our AI to transcribe them for you?"
              suggestionText="These lyrics were transcribed by our AI."
              noSuggestionText="No lyrics detected during transcription."
              editWarningText={
                <>
                  You can edit the lyrics after accepting{' '}
                  <MdCheck
                    color="var(--green-9)"
                    size="16"
                    style={{
                      marginBottom: '0.1rem',
                    }}
                  />{' '}
                  or rejecting the AI transcription.
                </>
              }
              lowConfidenceText="An AI transcription was generated, but did not meet our quality standards and is not available for editing."
              generateButtonLabel="Transcribe"
            />
            <Dialog.Root open={showFeedback} onOpenChange={setShowFeedback}>
              <FeedbackDialog
                {...dialogProps}
                onConfirm={selection => {
                  handleRejectTranscription(selection)
                  setShowFeedback(false)
                }}
              />
            </Dialog.Root>
          </>
        ) : (
          <Flex direction="column" gap="1">
            <Text as="label" size="1" color="gray" htmlFor="lyrics">
              {disabled ? 'Lyrics' : 'Add Lyrics'}
            </Text>
            <TextArea id="lyrics" {...textAreaProps} />
          </Flex>
        )}
      </Box>
      <DialogFooter
        primary={
          !disabled && (
            <ButtonContainer>
              {!hideCopyMetadata && (
                <CopyMetadataButton trackIds={[track.id]} />
              )}
              <Button
                appearance="primary"
                caption="Save"
                progressCaption="Saving lyrics"
                progressIndicator={saving}
                onClick={handleSubmit}
                disabled={aiBusy || saving || !changed || isAiSource}
              />
            </ButtonContainer>
          )
        }
        onClose={onClose}
      />
    </>
  )
}

export default EditLyrics
